import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SaveIcon from '@mui/icons-material/Save';

import defaultClient from '@src/api/clients/default';
import { toast } from '@src/components/core/toaster';
import { useEffectiveUser } from '@src/components/layout/AppLayout/ClientViewSwitcher';

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Admin' },
];

const ROLE_LABELS = {
  viewer: 'Viewer',
  editor: 'Editor',
  admin: 'Admin',
};

const ROLE_COLORS = {
  viewer: 'default',
  editor: 'info',
  admin: 'warning',
};

const INITIAL_FORM = {
  email: '',
  firstName: '',
  lastName: '',
  role: 'viewer',
};

function TeamManagementPage() {
  const user = useEffectiveUser();
  const memberships = user?.client_memberships || [];
  const isClientMember = !user?.is_staff && !user?.is_superuser && memberships.length > 0;

  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [sending, setSending] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [removeDialog, setRemoveDialog] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [leaveDialog, setLeaveDialog] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [updatingRole, setUpdatingRole] = useState(false);
  const [resending, setResending] = useState(null);

  // Billing contacts state
  const [billingContacts, setBillingContacts] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingSaving, setBillingSaving] = useState(false);
  const [removeContactDialog, setRemoveContactDialog] = useState(null);
  const [removingContact, setRemovingContact] = useState(false);

  const fetchTeam = useCallback(() => {
    setLoading(true);
    defaultClient
      .request({ url: 'accounts/auth/team/', method: 'GET' })
      .then((response) => {
        setTeamData(response?.data || null);
      })
      .catch((error) => {
        const detail = error?.response?.data?.detail;
        if (detail) {
          toast.error(detail);
        } else {
          toast.error('Could not load team members.');
        }
        setTeamData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isClientMember) {
      fetchTeam();
    } else {
      setLoading(false);
    }
  }, [isClientMember, fetchTeam]);

  const callerIsAdmin = teamData?.caller_role === 'admin';
  const callerDomain = teamData?.caller_domain || '';
  const members = teamData?.members || [];
  const scopeName = teamData?.scope_name || '';
  const scopeId = teamData?.scope_id || null;
  const scopeType = teamData?.scope || null;
  const memberCount = teamData?.member_count || 0;
  const memberLimit = teamData?.member_limit || 8;
  const atLimit = memberCount >= memberLimit;

  // Billing contacts helpers
  const fetchBillingContacts = useCallback(() => {
    if (!scopeId || scopeType !== 'client') return;
    setBillingLoading(true);
    defaultClient
      .request({
        url: 'dashboard/client-pocs/',
        method: 'GET',
        payload: { client_id: scopeId, page_size: 200 },
      })
      .then((response) => {
        const rows = Array.isArray(response?.data)
          ? response.data
          : response?.data?.results || [];
        setBillingContacts(
          rows.map((row) => ({
            id: row.id,
            first_name: row.first_name || '',
            last_name: row.last_name || '',
            email: row.email || '',
            receives_billing_notifications: row.receives_billing_notifications !== false,
            is_editing: false,
          }))
        );
      })
      .catch(() => {
        toast.error('Could not load billing contacts.');
      })
      .finally(() => setBillingLoading(false));
  }, [scopeId, scopeType]);

  useEffect(() => {
    if (scopeId && scopeType === 'client' && callerIsAdmin) {
      fetchBillingContacts();
    }
  }, [scopeId, scopeType, callerIsAdmin, fetchBillingContacts]);

  const addBillingContact = useCallback(() => {
    setBillingContacts((prev) => [
      ...prev,
      {
        id: null,
        first_name: '',
        last_name: '',
        email: '',
        receives_billing_notifications: true,
        is_editing: true,
      },
    ]);
  }, []);

  const updateBillingContact = useCallback((index, field, value) => {
    setBillingContacts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const enableBillingContactEditing = useCallback((index) => {
    setBillingContacts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], is_editing: true };
      return next;
    });
  }, []);

  const saveBillingContacts = useCallback(() => {
    if (!scopeId) return;

    const nonEmpty = billingContacts.filter(
      (c) => String(c.email || '').trim() || String(c.first_name || '').trim()
    );
    const invalid = nonEmpty.find((c) => !String(c.email || '').trim());
    if (invalid) {
      toast.error('Each billing contact requires an email address.');
      return;
    }

    setBillingSaving(true);
    const operations = nonEmpty.map((contact) => {
      const payload = {
        client: scopeId,
        first_name: String(contact.first_name || '').trim(),
        last_name: String(contact.last_name || '').trim(),
        email: String(contact.email || '').trim(),
        role: 'billing',
        active: true,
        receives_billing_notifications: contact.receives_billing_notifications !== false,
      };
      if (contact.id) {
        return defaultClient.request({
          url: `dashboard/client-pocs/${contact.id}/`,
          method: 'PATCH',
          payload,
        });
      }
      return defaultClient.request({
        url: 'dashboard/client-pocs/',
        method: 'POST',
        payload,
      });
    });

    Promise.all(operations)
      .then(() => {
        toast.success('Billing contacts saved.');
        fetchBillingContacts();
      })
      .catch(() => {
        toast.error('Failed to save billing contacts.');
      })
      .finally(() => setBillingSaving(false));
  }, [scopeId, billingContacts, fetchBillingContacts]);

  const confirmRemoveContact = useCallback(() => {
    if (!removeContactDialog) return;
    setRemovingContact(true);
    if (removeContactDialog.id) {
      defaultClient
        .request({
          url: `dashboard/client-pocs/${removeContactDialog.id}/`,
          method: 'DELETE',
        })
        .then(() => {
          toast.success('Billing contact removed.');
          setRemoveContactDialog(null);
          fetchBillingContacts();
        })
        .catch(() => {
          toast.error('Failed to remove billing contact.');
        })
        .finally(() => setRemovingContact(false));
    } else {
      // New unsaved contact — just remove from local state
      setBillingContacts((prev) => prev.filter((c) => c !== removeContactDialog));
      setRemoveContactDialog(null);
      setRemovingContact(false);
    }
  }, [removeContactDialog, fetchBillingContacts]);

  const handleInputChange = useCallback(
    (field) => (event) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    },
    []
  );

  const handleInvite = useCallback(
    (event) => {
      event.preventDefault();
      if (!form.email.trim()) {
        toast.error('Email is required.');
        return;
      }

      const emailDomain = form.email.split('@')[1]?.toLowerCase() || '';
      if (emailDomain !== callerDomain) {
        // Don't block on frontend — let backend handle free domain approval flow
      }

      setSending(true);
      defaultClient
        .request({
          url: 'accounts/auth/team/invite/',
          method: 'POST',
          payload: {
            email: form.email.trim(),
            first_name: form.firstName.trim(),
            last_name: form.lastName.trim(),
            role: form.role,
          },
        })
        .then((response) => {
          const data = response?.data;
          if (data?.approval_requested) {
            toast.success(
              'Your invite request has been submitted for approval. Our team will review it shortly.'
            );
          } else if (data?.invite_email_sent) {
            toast.success(`Invite sent to ${data.email}`);
          } else if (data?.membership_created) {
            toast.success(`${data.email} was added to the team.`);
          } else {
            toast.success(`${data.email} already has access. Role updated.`);
          }
          setForm(INITIAL_FORM);
          setShowInviteForm(false);
          fetchTeam();
        })
        .catch((error) => {
          const detail = error?.response?.data?.detail;
          toast.error(detail || 'Unable to send invite.');
        })
        .finally(() => setSending(false));
    },
    [form, callerDomain, fetchTeam]
  );

  const handleRemove = useCallback(() => {
    if (!removeDialog) return;
    setRemoving(true);
    defaultClient
      .request({
        url: `accounts/auth/team/${removeDialog.membership_id}/`,
        method: 'DELETE',
      })
      .then(() => {
        toast.success(`Removed ${removeDialog.email} from the team.`);
        setRemoveDialog(null);
        fetchTeam();
      })
      .catch((error) => {
        const detail = error?.response?.data?.detail;
        toast.error(detail || 'Unable to remove member.');
      })
      .finally(() => setRemoving(false));
  }, [removeDialog, fetchTeam]);

  const handleLeave = useCallback(() => {
    setLeaving(true);
    defaultClient
      .request({
        url: 'accounts/auth/team/leave/',
        method: 'POST',
      })
      .then(() => {
        toast.success('You have left the team. Redirecting...');
        setLeaveDialog(false);
        // Reload to trigger re-auth and redirect
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      })
      .catch((error) => {
        const detail = error?.response?.data?.detail;
        toast.error(detail || 'Unable to leave team.');
      })
      .finally(() => setLeaving(false));
  }, []);

  const handleResendInvite = useCallback(
    (membershipId, email) => {
      setResending(membershipId);
      defaultClient
        .request({
          url: `accounts/auth/team/${membershipId}/resend/`,
          method: 'POST',
        })
        .then(() => {
          toast.success(`Invite resent to ${email}`);
        })
        .catch((error) => {
          const detail = error?.response?.data?.detail;
          toast.error(detail || 'Unable to resend invite.');
        })
        .finally(() => setResending(null));
    },
    []
  );

  const handleRoleChange = useCallback(
    (membershipId, newRole) => {
      setUpdatingRole(true);
      defaultClient
        .request({
          url: `accounts/auth/team/${membershipId}/role/`,
          method: 'PATCH',
          payload: { role: newRole },
        })
        .then(() => {
          toast.success('Role updated.');
          setEditingRole(null);
          fetchTeam();
        })
        .catch((error) => {
          const detail = error?.response?.data?.detail;
          toast.error(detail || 'Unable to update role.');
        })
        .finally(() => setUpdatingRole(false));
    },
    [fetchTeam]
  );

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!isClientMember) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">This page is available to client portal members only.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4">Team Management</Typography>
        <Typography color="text.secondary">
          {scopeName
            ? `Manage portal access for ${scopeName}.`
            : 'Manage who has access to your client portal.'}
          {callerDomain
            ? ` New invites are limited to @${callerDomain} email addresses.`
            : ''}
        </Typography>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && teamData && (
        <>
          {/* Invite section for admins */}
          {callerIsAdmin && (
            <Box sx={{ mb: 3 }}>
              {!showInviteForm ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setShowInviteForm(true)}
                    disabled={atLimit}
                  >
                    Invite team member
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {memberCount} / {memberLimit} members
                  </Typography>
                  {atLimit && (
                    <Chip
                      label="Limit reached"
                      color="warning"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              ) : (
                <Card component="form" onSubmit={handleInvite}>
                  <CardHeader
                    title="Invite a team member"
                    subheader={`Only @${callerDomain} email addresses can be added directly. Other domains will require Lead Gurus approval.`}
                  />
                  <CardContent>
                    <Stack spacing={2}>
                      {atLimit && (
                        <Alert severity="warning">
                          Your team has reached the maximum of {memberLimit} members. Contact
                          Lead Gurus support to increase your limit.
                        </Alert>
                      )}
                      <TextField
                        label="Email"
                        value={form.email}
                        onChange={handleInputChange('email')}
                        type="email"
                        required
                        fullWidth
                        placeholder={`colleague@${callerDomain}`}
                        disabled={atLimit}
                      />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                          label="First name"
                          value={form.firstName}
                          onChange={handleInputChange('firstName')}
                          fullWidth
                          disabled={atLimit}
                        />
                        <TextField
                          label="Last name"
                          value={form.lastName}
                          onChange={handleInputChange('lastName')}
                          fullWidth
                          disabled={atLimit}
                        />
                      </Stack>
                      <TextField
                        label="Role"
                        value={form.role}
                        onChange={handleInputChange('role')}
                        select
                        fullWidth
                        helperText="Viewers can see dashboards. Editors can also update onboarding and assets. Admins can manage the team."
                        disabled={atLimit}
                      >
                        {ROLE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Stack direction="row" spacing={1}>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SendIcon />}
                          disabled={sending || atLimit}
                        >
                          {sending ? 'Sending...' : 'Send invite'}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setShowInviteForm(false);
                            setForm(INITIAL_FORM);
                          }}
                          disabled={sending}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}

          {!callerIsAdmin && (
            <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
              <Alert severity="info" sx={{ flex: 1 }}>
                Only team admins can invite or remove members. Contact your admin if you need to
                add someone.
              </Alert>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<LogoutIcon />}
                onClick={() => setLeaveDialog(true)}
              >
                Leave team
              </Button>
            </Stack>
          )}

          <Card>
            <CardHeader
              title="Team members"
              subheader={`${memberCount} of ${memberLimit} members`}
            />
            <Divider />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    {callerIsAdmin && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((member) => {
                    const isCurrentUser = member.id === user?.id;
                    const fullName =
                      [member.first_name, member.last_name].filter(Boolean).join(' ') || '-';
                    const hasLoggedIn = Boolean(member.last_login);

                    return (
                      <TableRow key={member.membership_id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={isCurrentUser ? 600 : 400}>
                            {fullName}
                            {isCurrentUser ? ' (you)' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{member.email}</Typography>
                        </TableCell>
                        <TableCell>
                          {editingRole === member.membership_id ? (
                            <TextField
                              select
                              size="small"
                              value={member.role}
                              disabled={updatingRole}
                              onChange={(e) =>
                                handleRoleChange(member.membership_id, e.target.value)
                              }
                              onBlur={() => setEditingRole(null)}
                              sx={{ minWidth: 110 }}
                            >
                              {ROLE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <Chip
                              label={ROLE_LABELS[member.role] || member.role}
                              color={ROLE_COLORS[member.role] || 'default'}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={hasLoggedIn ? 'Active' : 'Invited'}
                            color={hasLoggedIn ? 'success' : 'default'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        {callerIsAdmin && (
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              {/* Resend invite for pending members */}
                              {!hasLoggedIn && !isCurrentUser && (
                                <Tooltip title="Resend invite email">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={resending === member.membership_id}
                                    onClick={() =>
                                      handleResendInvite(member.membership_id, member.email)
                                    }
                                  >
                                    <RefreshIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {/* Edit role & remove for non-self members */}
                              {!isCurrentUser && (
                                <>
                                  <Tooltip title="Change role">
                                    <IconButton
                                      size="small"
                                      onClick={() => setEditingRole(member.membership_id)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Remove member">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => setRemoveDialog(member)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {!members.length && (
                    <TableRow>
                      <TableCell colSpan={callerIsAdmin ? 5 : 4} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No team members found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Billing contacts section — admins only, client scope only */}
          {callerIsAdmin && scopeType === 'client' && (
            <Card sx={{ mt: 3 }}>
              <CardHeader
                title="Billing Contacts"
                subheader="People who receive invoice emails. Add or update contacts so the right people get notified."
                action={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={addBillingContact}
                      disabled={billingLoading || billingSaving}
                    >
                      Add contact
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={saveBillingContacts}
                      disabled={billingLoading || billingSaving || !billingContacts.length}
                    >
                      {billingSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </Stack>
                }
              />
              <Divider />
              <CardContent>
                {billingLoading && <LinearProgress sx={{ mb: 2 }} />}
                {!billingLoading && !billingContacts.length && (
                  <Alert severity="info">
                    No billing contacts yet. Add one so invoice emails reach the right people.
                  </Alert>
                )}
                <Stack spacing={2}>
                  {billingContacts.map((contact, index) => (
                    <Paper key={contact.id || `new-${index}`} variant="outlined" sx={{ p: 2 }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ sm: 'center' }}
                      >
                        <TextField
                          label="First name"
                          size="small"
                          value={contact.first_name}
                          onChange={(e) =>
                            updateBillingContact(index, 'first_name', e.target.value)
                          }
                          disabled={billingLoading || billingSaving || !contact.is_editing}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Last name"
                          size="small"
                          value={contact.last_name}
                          onChange={(e) =>
                            updateBillingContact(index, 'last_name', e.target.value)
                          }
                          disabled={billingLoading || billingSaving || !contact.is_editing}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Email"
                          size="small"
                          value={contact.email}
                          onChange={(e) =>
                            updateBillingContact(index, 'email', e.target.value)
                          }
                          disabled={billingLoading || billingSaving || !contact.is_editing}
                          sx={{ flex: 1.5 }}
                        />
                        <FormControlLabel
                          sx={{ m: 0, whiteSpace: 'nowrap' }}
                          control={
                            <Checkbox
                              size="small"
                              checked={contact.receives_billing_notifications !== false}
                              onChange={(e) =>
                                updateBillingContact(
                                  index,
                                  'receives_billing_notifications',
                                  e.target.checked
                                )
                              }
                              disabled={billingLoading || billingSaving || !contact.is_editing}
                            />
                          }
                          label={<Typography variant="body2">Receives invoices</Typography>}
                        />
                        <Stack direction="row" spacing={0.5}>
                          {!contact.is_editing && (
                            <Tooltip title="Edit contact">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => enableBillingContactEditing(index)}
                                disabled={billingLoading || billingSaving}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Remove contact">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setRemoveContactDialog(contact)}
                              disabled={billingLoading || billingSaving}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Remove billing contact confirmation dialog */}
      <Dialog
        open={Boolean(removeContactDialog)}
        onClose={() => setRemoveContactDialog(null)}
      >
        <DialogTitle>Remove billing contact?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove{' '}
            <strong>
              {removeContactDialog
                ? [removeContactDialog.first_name, removeContactDialog.last_name]
                    .filter(Boolean)
                    .join(' ') || removeContactDialog.email
                : ''}
            </strong>{' '}
            from billing contacts? They will no longer receive invoice emails.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveContactDialog(null)} disabled={removingContact}>
            Cancel
          </Button>
          <Button
            onClick={confirmRemoveContact}
            color="error"
            variant="contained"
            disabled={removingContact}
          >
            {removingContact ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove confirmation dialog */}
      <Dialog open={Boolean(removeDialog)} onClose={() => setRemoveDialog(null)}>
        <DialogTitle>Remove team member?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove{' '}
            <strong>
              {removeDialog
                ? [removeDialog.first_name, removeDialog.last_name].filter(Boolean).join(' ') ||
                  removeDialog.email
                : ''}
            </strong>{' '}
            from the portal? They will lose access immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialog(null)} disabled={removing}>
            Cancel
          </Button>
          <Button onClick={handleRemove} color="error" variant="contained" disabled={removing}>
            {removing ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Leave team confirmation dialog */}
      <Dialog open={leaveDialog} onClose={() => setLeaveDialog(false)}>
        <DialogTitle>Leave team?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to leave{' '}
            <strong>{scopeName || 'this team'}</strong>? You will lose access to the portal
            immediately. To regain access, an admin will need to invite you again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveDialog(false)} disabled={leaving}>
            Cancel
          </Button>
          <Button onClick={handleLeave} color="error" variant="contained" disabled={leaving}>
            {leaving ? 'Leaving...' : 'Leave team'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

TeamManagementPage.displayName = 'TeamManagementPage';

export default TeamManagementPage;
